import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TableContainer from "components/common/TableContainer";
import {
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { toast } from "react-toastify";
import {
  deleteEnquirySiteVisit,
  getEnquirySiteVisits,
} from "features/EnquirySiteVisits/services/EnquirySiteVisitService";
import type { EnquirySiteVisit } from "features/EnquirySiteVisits/types/enquiry-site-visit.types";
import {
  EnquirySiteVisitPriorityMap,
  EnquirySiteVisitStatus,
  EnquirySiteVisitStatusMap,
} from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import { runEnquirySiteVisitStart } from "features/EnquirySiteVisits/enquirySiteVisitWorkflow";
import {
  getSiteVisitWorkflowUiVisibility,
  guardSiteVisitCancel,
  guardSiteVisitComplete,
  guardSiteVisitStart,
} from "features/EnquirySiteVisits/siteVisitWorkflowAccess";
import EnquirySiteVisitWorkflowModals from "features/EnquirySiteVisits/EnquirySiteVisitWorkflowModals";
import DeleteModal from "components/common/DeleteModal";
import SiteVisitAssigneesAvatarGroup from "features/EnquirySiteVisits/EnquirySiteVisitList/SiteVisitAssigneesAvatarGroup";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import {
  canReassignSiteVisit as isSiteVisitReassignable,
  shouldShowSiteVisitActionButtons,
} from "features/EnquirySiteVisits/utils/siteVisitActionPolicy";
import { hasAnyEnquirySiteVisitEditPermission } from "features/EnquirySiteVisits/utils/enquirySiteVisitEditFieldPolicy";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { useEnquiryOverview } from "./EnquiryOverviewContext";
import { EnquiryStatus } from "features/Enquiries/constants/enquiry.constants";

function canDeleteSiteVisit(status: string | null | undefined): boolean {
  const st = String(status ?? "").toUpperCase();
  return st !== EnquirySiteVisitStatus.IN_PROGRESS && st !== EnquirySiteVisitStatus.COMPLETED;
}

const SiteVisitsTab = () => {
  const { enquiry } = useEnquiryOverview();
  const { isEmployee } = useProfile();
  const { can } = useAccess();
  const canCreateSiteVisit = can(Permissions.SITE_VISIT.CREATE);
  const canReadSiteVisit = can(Permissions.SITE_VISIT.READ);
  const canAssignSiteVisit = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canReassignSiteVisit = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  const tableRef = useRef<() => void>(null);
  const refreshTable = useCallback(() => tableRef.current?.(), []);

  const [visitToDelete, setVisitToDelete] = useState<EnquirySiteVisit | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [workflowModalVisit, setWorkflowModalVisit] = useState<EnquirySiteVisit | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busyVisitId, setBusyVisitId] = useState<number | null>(null);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormVisit, setAssignFormVisit] = useState<EnquirySiteVisit | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);

  const requestParams = useMemo(
    () => ({ enquiryId: enquiry?.id ?? 0 }),
    [enquiry?.id],
  );

  const onClickDelete = useCallback((r: EnquirySiteVisit) => {
    setVisitToDelete(r);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (!visitToDelete) return;
    try {
      const res = await deleteEnquirySiteVisit(visitToDelete.id);
      if (res.success) {
        toast.success(res.message);
        refreshTable();
      }
      setDeleteModal(false);
    } catch (err) {
      toast.error((err as any)?.response?.data?.message ?? "Delete failed");
    }
  };

  const handleStartVisit = useCallback(
    async (r: EnquirySiteVisit) => {
      if (!guardSiteVisitStart(r, isEmployee)) return;
      setBusyVisitId(r.id);
      try {
        const ok = await runEnquirySiteVisitStart(r.id);
        if (ok) refreshTable();
      } finally {
        setBusyVisitId(null);
      }
    },
    [isEmployee, refreshTable],
  );

  const openCompleteModal = useCallback(
    (r: EnquirySiteVisit) => {
      if (!guardSiteVisitComplete(r, isEmployee)) return;
      setWorkflowModalVisit(r);
      setCompleteOpen(true);
    },
    [isEmployee],
  );

  const openCancelModal = useCallback(
    (r: EnquirySiteVisit) => {
      if (!guardSiteVisitCancel(r.status, isEmployee)) return;
      setWorkflowModalVisit(r);
      setCancelOpen(true);
    },
    [isEmployee],
  );

  const openAssign = useCallback((r: EnquirySiteVisit) => {
    setAssignFormIsReassign(false);
    setAssignFormVisit(r);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((r: EnquirySiteVisit) => {
    if (!isSiteVisitReassignable(r.status)) {
      toast.info("Completed or cancelled site visits cannot be reassigned.");
      return;
    }
    setAssignFormIsReassign(true);
    setAssignFormVisit(r);
    setAssignFormOpen(true);
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormVisit(null);
    setAssignFormIsReassign(false);
  }, []);

  const columns: ColumnDef<EnquirySiteVisit>[] = useMemo(
    () => [
      IndexColumn<EnquirySiteVisit>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Link
            to={`/enquiry-management/site-visits/${row.original.id}`}
            className="text-decoration-none link"
          >
            <CodeCell code={row.original.code} />
          </Link>
        ),
      },
      {
        header: "Priority",
        accessorKey: "priority",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.priority;
          return p ? (
            <StatusBadge value={p} map={EnquirySiteVisitPriorityMap} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Assigned",
        id: "assignedUsers",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const canAssignMore =
            shouldShowSiteVisitActionButtons(record.status) && canAssignSiteVisit;
          return (
            <SiteVisitAssigneesAvatarGroup
              siteVisit={record}
              assignments={record.assignments}
              siteVisitId={record.id}
              canReadDetail={canReadSiteVisit}
              canAssignMore={canAssignMore}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge value={row.original.status} map={EnquirySiteVisitStatusMap} />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            "—"
          ),
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const isActionable = shouldShowSiteVisitActionButtons(record.status);
          const canEditVisit = hasAnyEnquirySiteVisitEditPermission(record.status);
          const canDelete = canDeleteSiteVisit(record.status);
          const { showStart, showComplete, showCancel, showAnyWorkflow } =
            getSiteVisitWorkflowUiVisibility(record.status, isEmployee);
          const rowBusy = busyVisitId === record.id;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Site visit actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem
                  tag={Link}
                  to={`/enquiry-management/site-visits/${record.id}`}
                >
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                {canEditVisit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/enquiry-management/site-visits/edit/${record.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {isActionable && (canAssignSiteVisit || canReassignSiteVisit) ? (
                  <>
                    <DropdownItem divider />
                    {canAssignSiteVisit ? (
                      <DropdownItem
                        disabled={rowBusy}
                        onClick={() => openAssign(record)}
                      >
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canReassignSiteVisit ? (
                      <DropdownItem
                        disabled={rowBusy}
                        onClick={() => void openReassign(record)}
                      >
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {isActionable && showAnyWorkflow ? (
                  <>
                    <DropdownItem divider />
                    {showStart ? (
                      <DropdownItem
                        disabled={rowBusy}
                        onClick={() => void handleStartVisit(record)}
                      >
                        <i className="ri-play-circle-line align-bottom me-2 text-primary" />
                        Start visit
                      </DropdownItem>
                    ) : null}
                    {showComplete ? (
                      <DropdownItem
                        disabled={rowBusy}
                        onClick={() => void openCompleteModal(record)}
                      >
                        <i className="ri-checkbox-circle-line align-bottom me-2 text-success" />
                        Complete visit
                      </DropdownItem>
                    ) : null}
                    {showCancel ? (
                      <DropdownItem
                        disabled={rowBusy}
                        onClick={() => openCancelModal(record)}
                      >
                        <i className="ri-close-circle-line align-bottom me-2 text-warning" />
                        Cancel visit
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {canDelete ? (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(record)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      busyVisitId,
      canAssignSiteVisit,
      canReassignSiteVisit,
      canReadSiteVisit,
      isEmployee,
      handleStartVisit,
      openAssign,
      openReassign,
      openCompleteModal,
      openCancelModal,
      onClickDelete,
    ],
  );

  return (
    <React.Fragment>
      <EnquirySiteVisitWorkflowModals
        visitId={workflowModalVisit?.id ?? null}
        completeOpen={completeOpen}
        cancelOpen={cancelOpen}
        onCloseComplete={() => {
          setCompleteOpen(false);
          setWorkflowModalVisit(null);
        }}
        onCloseCancel={() => {
          setCancelOpen(false);
          setWorkflowModalVisit(null);
        }}
        onSuccess={refreshTable}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <WorkAssignmentForm
        show={assignFormOpen && assignFormVisit != null}
        sourceType={WorkAssignmentSourceType.SITE_VISIT}
        sourceId={assignFormVisit?.id ?? 0}
        sourceLabel={
          assignFormVisit
            ? assignFormVisit.code
              ? `Site visit ${assignFormVisit.code}`
              : `Site visit #${assignFormVisit.id}`
            : undefined
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={refreshTable}
      />

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Site Visits</h5>
          {canCreateSiteVisit && enquiry && enquiry.status === EnquiryStatus.NEW ? (
            <Link
              to={`/enquiry-management/enquiries/${enquiry.id}/site-visits/create`}
              state={{ defaultEnquiry: enquiry }}
              className="btn btn-primary btn-sm"
            >
              <i className="ri-add-line align-bottom me-1" />
              Create site visit
            </Link>
          ) : null}
        </CardHeader>
        <CardBody className="pt-0">
          <TableContainer
            columns={columns}
            fetchApi={getEnquirySiteVisits}
            requestParams={requestParams}
            pageSize={10}
            globalSearch={false}
            refreshRef={tableRef}
            divClass="table-responsive table-card mb-3"
            tableClass="align-middle table-nowrap mb-0"
            theadClass="table-light text-muted"
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default SiteVisitsTab;
